import { getAxiosData } from '@internal/utils/api/axios';
import { MachineDealRoomData } from '@internal/utils/DealRoomData';
import { MachineNotFoundError } from '@internal/utils/errors/machineErrors';
import { WebsiteForm } from '@internal/utils/form';
import { TLocale } from '@internal/utils/machine/i18n/Locale';
import {
    MachineMeta,
    MachineMetaSchema,
} from '@internal/utils/machine/Machine';
import { MachineType } from '@internal/utils/machine/MachineType';
import { SearchPayload } from '@internal/utils/search/SearchPayload';
import { SortOrder } from '@internal/utils/search/SortOrder';
import { CountryCodeSchema } from '@internal/utils/user/User';
import axios, { AxiosError } from 'axios';
import { environment } from 'environment/environment';
import { SetOptional } from 'type-fest';
import { z } from 'zod';
import { TSearchResult } from './TSearchResult';
import { GetSuggestResponse } from './types/suggestTypes';
import { TFiltersConfiguration } from '@internal/blocks/SearchResultsBlock/TFiltersConfiguration';

const apiClient = axios.create({
    baseURL: `${environment.OWN_URL}/api`,
    timeout: 30000,
});

export const getMachineMeta = async (params: {
    machineGroupId: string;
    resolveMerged?: boolean;
}): Promise<MachineMeta> => {
    return apiClient
        .get(`/getMachineMeta/`, { params })
        .then(res => MachineMetaSchema.parse(res.data))
        .catch(err => {
            const axiosError = err as AxiosError;
            if (
                axiosError.isAxiosError &&
                axiosError.response?.status === 404
            ) {
                throw new MachineNotFoundError(params.machineGroupId);
            }
            throw err;
        });
};

const SerpUrlForAlternativesResponseSchema = z
    .object({
        data: z.object({
            url: z.string().nonempty(),
        }),
    })
    .transform(res => res.data);

export const getSerpUrlForAlternatives = async (params: {
    userCountryCode: string;
    disableCache: boolean;
    forceUpdateCache: boolean;
    locale: TLocale;
    machine: MachineMeta;
}) =>
    apiClient
        .post(`/getSerpUrlForAlternatives/`, params)
        .then(SerpUrlForAlternativesResponseSchema.parse);

export const getMachinesCount = (params: {
    search: SetOptional<SearchPayload, 'limit' | 'offset' | 'sort'>;
    abortSignal?: AbortSignal;
    countryCode: string;
}) =>
    searchMachines({
        ...params,
        search: {
            ...params.search,
            limit: 1,
            offset: 0,
            sort: { by: 'relevance', order: SortOrder.Ascending },
        },
    }).then(res => res.count);

export const searchMachines = async ({
    search,
    abortSignal,
    countryCode,
}: {
    search: SearchPayload;
    abortSignal?: AbortSignal;
    countryCode: string;
}) =>
    apiClient
        .post<TSearchResult>(
            `/searchMachines/`,
            {
                search,
                countryCode,
            },
            {
                signal: abortSignal,
            },
        )
        .then(getAxiosData);

export const suggest = ({
    abortSignal,
    ...params
}: {
    query: string;
    locale: TLocale;
    countryCode: string;
    BRAND?: string;
    MACHINE_TYPE?: string;
    abortSignal: AbortSignal;
}): Promise<GetSuggestResponse> =>
    apiClient
        .post<GetSuggestResponse>(`/suggest/`, params, {
            signal: abortSignal,
        })
        .then(getAxiosData);

export const postForm = (data: WebsiteForm): Promise<{ success: boolean }> =>
    apiClient.post(`/forms/`, data).then(res => res.data);

const GetUserCountryCodeResponseSchema = z
    .object({
        data: z.object({
            userCountryCode: CountryCodeSchema,
        }),
    })
    .transform(res => res.data.userCountryCode);
export const getUserCountryCode = async () =>
    apiClient
        .get(`/getUserCountryCode/`)
        .then(GetUserCountryCodeResponseSchema.parse)
        .catch(() => null);

export const getDealRoomData = async (params: {
    userId: string;
    machineGroupId: string;
    locale: TLocale;
}) =>
    apiClient
        .get<MachineDealRoomData>(`/getDealRoomData/`, { params })
        .then(getAxiosData);

export const getInspectionDisplayDefinition = async (params: {
    machineType: MachineType;
    locale: TLocale;
}) =>
    apiClient
        .get(`/getInspectionDisplayDefinition/`, { params })
        .then(getAxiosData);

export const getSearchFilters = async (
    params: {
        machineType: MachineType;
        locale: TLocale;
    },
    abortSignal?: AbortSignal,
) =>
    apiClient
        .get<TFiltersConfiguration>(`/getSearchFilters/`, {
            params,
            signal: abortSignal,
        })
        .then(getAxiosData);
